import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Scan, CheckCircle, X, RefreshCw, Layers, Send, Trash2,
  CheckCircle2, AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

/**
 * Slice D9.2 — Per-section ComboV2 PP Station (scan-USN flow).
 *
 * Operator workflow:
 *   1. Pick a plan from the dropdown (only PICKING / DISTRIBUTED / ASSEMBLING).
 *   2. Pick a session (combo cell) within the plan.
 *   3. Validate Staff ID.
 *   4. Scan USNs one-by-one (each goes through /scan-usn).
 *   5. Submit the batch — server computes combos = min(count per component),
 *      creates combo USNs, marks consumed.
 *
 * Salem-untouched: hits ONLY /api/per-section/{slug}/combo-v2-* endpoints.
 */

const SLUG_TO_DISPLAY = {
  color_combo: 'Color Combo Section',
  art_silk: 'Art Silk Section',
  kids: 'Kids Section',
};

const ACTIVE_PLAN_STATUSES = ['PICKING', 'DISTRIBUTED', 'ASSEMBLING'];

const PerSectionPpStationClone = () => {
  const navigate = useNavigate();
  const { section: slug } = useParams();
  const sectionName = SLUG_TO_DISPLAY[slug] || slug;
  const backRoute = `/sections/dispatch/${slug}`;
  const apiBase = `/per-section/${slug}/combo-v2-plans`;
  const sessionApiBase = `/per-section/${slug}/combo-v2-sessions`;

  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const session = sessions.find((s) => s.session_id === selectedSessionId) || null;

  const [staffId, setStaffId] = useState('');
  const [staffValidated, setStaffValidated] = useState(false);
  const [staffName, setStaffName] = useState('');

  const [usnInput, setUsnInput] = useState('');
  const [scannedUsns, setScannedUsns] = useState([]); // [{usn, component_barcode, component_name, bundle_code}]
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const usnInputRef = useRef(null);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const all = await Promise.all(
        ACTIVE_PLAN_STATUSES.map((s) =>
          apiClient.get(`${apiBase}?status=${s}`).then((r) => r.data.plans || []).catch(() => [])
        )
      );
      const merged = [].concat(...all);
      setPlans(merged);
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  const loadSessions = useCallback(async (planId) => {
    if (!planId) {
      setSessions([]);
      return;
    }
    try {
      const r = await apiClient.get(`${apiBase}/${planId}/sessions`);
      setSessions(r.data.sessions || []);
    } catch (error) {
      setSessions([]);
    }
  }, [apiBase]);

  useEffect(() => { loadPlans(); }, [loadPlans]);
  useEffect(() => {
    loadSessions(selectedPlanId);
    setSelectedSessionId('');
    setScannedUsns([]);
  }, [selectedPlanId, loadSessions]);

  const handleStaffValidate = async () => {
    if (!staffId.trim()) { toast.error('Enter Staff ID'); return; }
    try {
      const response = await apiClient.get(`/validate/staff/${staffId}`);
      if (response.data.valid) {
        setStaffValidated(true);
        setStaffName(response.data.staff_name);
        toast.success(`Staff: ${response.data.staff_name}`);
        setTimeout(() => usnInputRef.current?.focus(), 100);
      } else {
        toast.error('Invalid Staff ID');
      }
    } catch (error) {
      toast.error('Staff validation failed');
    }
  };

  const handleScanUsn = async () => {
    const usn = usnInput.trim();
    if (!usn) return;
    if (!selectedSessionId) { toast.error('Pick a session first'); return; }
    if (scannedUsns.find((s) => s.usn === usn)) {
      toast.error(`USN ${usn} already in batch`);
      setUsnInput('');
      return;
    }
    try {
      const response = await apiClient.post(
        `${sessionApiBase}/${selectedSessionId}/scan-usn`,
        { usn_code: usn },
      );
      setScannedUsns((prev) => [
        ...prev,
        {
          usn,
          component_barcode: response.data.component_barcode,
          component_name: response.data.component_name,
          bundle_code: response.data.bundle_code,
        },
      ]);
      toast.success(`Scanned ${response.data.component_name || response.data.component_barcode}`);
      setUsnInput('');
      setTimeout(() => usnInputRef.current?.focus(), 50);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Scan failed');
    }
  };

  const handleRemoveUsn = (usn) => {
    setScannedUsns((prev) => prev.filter((s) => s.usn !== usn));
  };

  const handleSubmitBatch = async () => {
    if (scannedUsns.length === 0) { toast.error('Scan some USNs first'); return; }
    if (!staffValidated) { toast.error('Validate staff first'); return; }
    setSubmitting(true);
    try {
      const response = await apiClient.post(
        `${sessionApiBase}/${selectedSessionId}/submit`,
        {
          staff_id: staffId,
          scanned_usns: scannedUsns.map((s) => s.usn),
        },
      );
      const body = response.data;
      toast.success(
        `+${body.combos_created} combos · session ${body.session_status}` +
          (body.plan_status === 'COMPLETED' ? ' · plan COMPLETED' : '')
      );
      // Refresh sessions + reset batch.
      setScannedUsns([]);
      await loadSessions(selectedPlanId);
      if (body.plan_status === 'COMPLETED') {
        await loadPlans();
        setSelectedPlanId('');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Component-wise count for current batch.
  const componentCounts = scannedUsns.reduce((acc, s) => {
    const key = s.component_barcode || 'unknown';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const sessionComponents = session?.components || [];

  /**
   * Checklist summary — drives the sticky badge bar at the top.
   * Each component contributes: (a) how many it still needs, scoped to
   * the remaining (target - assembled) combos, and (b) an OK / SHORT /
   * EXTRA flag based on what's currently in the scan batch.
   */
  const targetTotal = session?.target_quantity || 0;
  const alreadyAssembled = session?.assembled_quantity || 0;
  const remainingCombos = Math.max(0, targetTotal - alreadyAssembled);
  const checklistRows = (() => {
    // Aggregate BOM rows by barcode — duplicate rows of the same component
    // (or a single row with qty>1) sum into ONE per-combo requirement.
    const requiredPerSku = {};
    const namePerSku = {};
    for (const c of sessionComponents) {
      const sku = c.component_barcode || c.barcode;
      if (!sku) continue;
      requiredPerSku[sku] = (requiredPerSku[sku] || 0) + (parseInt(c.quantity, 10) || 1);
      if (!namePerSku[sku]) namePerSku[sku] = c.component_name || sku;
    }
    return Object.keys(requiredPerSku).map((sku) => {
      const perCombo = requiredPerSku[sku];
      const needed = remainingCombos * perCombo;
      const scanned = componentCounts[sku] || 0;
      let status = 'short';
      if (needed === 0) status = 'idle';
      else if (scanned === needed) status = 'ready';
      else if (scanned > needed) status = 'extra';
      return { sku, name: namePerSku[sku], perCombo, needed, scanned, status };
    });
  })();
  const combosProducible = checklistRows.length === 0
    ? 0
    : Math.min(...checklistRows.map((r) => Math.floor(r.scanned / r.perCombo)));
  const allReady =
    checklistRows.length > 0 &&
    checklistRows.every((r) => r.status === 'ready') &&
    combosProducible === remainingCombos;

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid={`per-section-pp-station-${slug}`}>
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate(backRoute)}
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">PP Station</h1>
              <p className="text-sm text-[#E8DCC8]">{sectionName} · ComboV2 scan-USN</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadPlans}
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-8 space-y-6">
        {staffValidated && session && session.status !== 'COMPLETED' && checklistRows.length > 0 && (
          <Card
            className={`border-2 sticky top-2 z-10 ${
              allReady ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50/60'
            }`}
            data-testid="pp-checklist-badge"
          >
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center justify-between text-base">
                <div className="flex items-center gap-2">
                  {allReady ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                  )}
                  Session Checklist
                </div>
                <Badge
                  className={`${
                    allReady
                      ? 'bg-emerald-600 text-white'
                      : 'bg-amber-600 text-white'
                  }`}
                  data-testid="pp-checklist-combos-badge"
                >
                  Combos: {combosProducible} / {remainingCombos} pending
                  {alreadyAssembled > 0 ? ` · ${alreadyAssembled} done` : ''}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {checklistRows.map((r) => {
                  const tone =
                    r.status === 'ready'
                      ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                      : r.status === 'extra'
                      ? 'bg-rose-100 text-rose-800 border-rose-300'
                      : r.status === 'idle'
                      ? 'bg-stone-100 text-stone-700 border-stone-300'
                      : 'bg-amber-100 text-amber-800 border-amber-300';
                  return (
                    <div
                      key={r.sku}
                      className={`flex items-center justify-between px-3 py-2 rounded-md border ${tone}`}
                      data-testid={`pp-checklist-row-${r.sku}`}
                    >
                      <div className="min-w-0">
                        <div className="font-medium truncate">{r.name}</div>
                        <div className="text-xs font-mono text-stone-500">
                          {r.sku}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 text-sm">
                        <span className="font-semibold">
                          {r.scanned}
                        </span>
                        <span className="text-stone-500">/ {r.needed}</span>
                        {r.status === 'ready' && (
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        )}
                        {r.status === 'extra' && (
                          <AlertCircle className="w-4 h-4 text-rose-600" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-fuchsia-600" />
              1. Select Plan & Session
            </CardTitle>
            <CardDescription>
              Showing plans in PICKING / DISTRIBUTED / ASSEMBLING.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Plan</Label>
              <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
                <SelectTrigger data-testid="plan-select">
                  <SelectValue placeholder="Pick a plan" />
                </SelectTrigger>
                <SelectContent>
                  {plans.length === 0 ? (
                    <SelectItem value="__none__" disabled>No active plans</SelectItem>
                  ) : (
                    plans.map((p) => (
                      <SelectItem key={p.plan_id} value={p.plan_id}>
                        {p.plan_id} · {p.status} · {p.bundle_count} bundles
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {selectedPlanId && (
              <div>
                <Label>Session (Combo Cell)</Label>
                <Select value={selectedSessionId} onValueChange={setSelectedSessionId}>
                  <SelectTrigger data-testid="session-select">
                    <SelectValue placeholder="Pick a session" />
                  </SelectTrigger>
                  <SelectContent>
                    {sessions.length === 0 ? (
                      <SelectItem value="__none__" disabled>No sessions in this plan</SelectItem>
                    ) : (
                      sessions.map((s) => (
                        <SelectItem key={s.session_id} value={s.session_id}>
                          {s.combo_name || s.combo_barcode} · size {s.size} ·{' '}
                          {s.assembled_quantity}/{s.target_quantity} ({s.status})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {session && (
              <div className="p-3 bg-fuchsia-50 rounded-lg text-sm space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{session.combo_name || session.combo_barcode}</span>
                  <Badge className="bg-fuchsia-100 text-fuchsia-700">
                    {session.assembled_quantity}/{session.target_quantity} combos
                  </Badge>
                </div>
                <p className="text-xs text-gray-600">
                  Components: {sessionComponents.map((c) => c.component_name || c.component_barcode || c.barcode).join(' + ')}
                </p>
                {session.status === 'COMPLETED' && (
                  <p className="text-xs text-green-700 font-medium">Session already completed.</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>2. Staff Validation</CardTitle>
          </CardHeader>
          <CardContent>
            {!staffValidated ? (
              <div className="flex gap-3">
                <Input
                  value={staffId}
                  onChange={(e) => setStaffId(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleStaffValidate()}
                  placeholder="Scan/enter Staff ID"
                  data-testid="staff-id-input"
                />
                <Button
                  onClick={handleStaffValidate}
                  className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                  data-testid="validate-staff-btn"
                >
                  Validate
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="font-medium">{staffName}</span>
                  <span className="text-sm text-gray-500">({staffId})</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setStaffValidated(false); setStaffId(''); setStaffName(''); }}
                  data-testid="change-staff-btn"
                >
                  Change
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {staffValidated && session && session.status !== 'COMPLETED' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scan className="w-5 h-5 text-blue-600" />
                3. Scan USNs
              </CardTitle>
              <CardDescription>
                Each component must be scanned in equal quantity. Combos =
                min(count per component).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-3">
                <Input
                  ref={usnInputRef}
                  value={usnInput}
                  onChange={(e) => setUsnInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleScanUsn()}
                  placeholder="Scan USN code"
                  data-testid="usn-input"
                />
                <Button
                  onClick={handleScanUsn}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="scan-btn"
                >
                  Add
                </Button>
              </div>

              {sessionComponents.length > 0 && (
                <div className="grid grid-cols-2 gap-2">
                  {sessionComponents.map((c) => {
                    const sku = c.component_barcode || c.barcode;
                    const cnt = componentCounts[sku] || 0;
                    return (
                      <div
                        key={sku}
                        className="flex justify-between items-center p-2 rounded border bg-gray-50"
                        data-testid={`component-count-${sku}`}
                      >
                        <span className="text-sm">{c.component_name || sku}</span>
                        <Badge className="bg-blue-100 text-blue-700">{cnt}</Badge>
                      </div>
                    );
                  })}
                </div>
              )}

              {scannedUsns.length > 0 && (
                <div>
                  <Label>Batch ({scannedUsns.length} USNs)</Label>
                  <div className="max-h-40 overflow-y-auto border rounded p-2 bg-gray-50 space-y-1 mt-1">
                    {scannedUsns.map((s) => (
                      <div
                        key={s.usn}
                        className="flex items-center justify-between text-sm"
                        data-testid={`scanned-${s.usn}`}
                      >
                        <span>
                          <span className="font-mono">{s.usn}</span>{' '}
                          <span className="text-gray-500">
                            ({s.component_name || s.component_barcode})
                          </span>
                        </span>
                        <button
                          onClick={() => handleRemoveUsn(s.usn)}
                          className="text-red-500 hover:text-red-700"
                          data-testid={`remove-${s.usn}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setScannedUsns([])}
                  disabled={scannedUsns.length === 0}
                  className="flex-1"
                  data-testid="clear-batch-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Clear
                </Button>
                <Button
                  onClick={handleSubmitBatch}
                  disabled={submitting || scannedUsns.length === 0}
                  className="flex-1 bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                  data-testid="submit-batch-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {submitting ? 'Submitting…' : `Submit (${scannedUsns.length})`}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default PerSectionPpStationClone;
